import { useEffect } from "react";
import { Link } from "react-router-dom";
import { applyPageSeo } from "../../lib/seo";

export function PrivacyPolicy() {
  useEffect(() => {
    return applyPageSeo({
      title: "Privacy Policy",
      description:
        "Privacy policy for React Email Builder. Learn how we collect, use, and protect your data when you use our AI email template builder.",
      path: "/legal/privacy",
      keywords: ["privacy policy", "react email builder privacy", "data protection"],
    });
  }, []);

  return (
    <div className="bg-white text-slate-950">
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white py-14 sm:py-18">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="mb-4 flex items-center gap-2 text-sm">
            <Link to="/" className="text-slate-400 transition hover:text-slate-600">Home</Link>
            <svg aria-hidden="true" className="h-3.5 w-3.5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            <span className="text-slate-700">Legal</span>
          </nav>
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-sky-700">Legal</p>
          <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Privacy Policy</h1>
          <p className="mt-4 text-base text-slate-600">Last updated: August 8, 2026</p>
        </div>
      </section>

      <section className="py-14 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none space-y-8 text-base leading-7 text-slate-600">
            <div>
              <h2 className="text-2xl font-bold text-slate-950">1. Information We Collect</h2>
              <p className="mt-3">We collect information you provide directly and information generated automatically when you use our service.</p>
              <h3 className="mt-6 text-lg font-semibold text-slate-950">1.1 Information You Provide</h3>
              <ul className="mt-3 ml-6 list-disc space-y-2">
                <li><strong className="text-slate-950">Account information:</strong> email address, password (hashed), display name</li>
                <li><strong className="text-slate-950">Billing information:</strong> processed by Stripe — we never see or store your card details</li>
                <li><strong className="text-slate-950">Content you create:</strong> email templates, drafts, and prompts you submit for AI generation</li>
                <li><strong className="text-slate-950">Newsletter subscriptions:</strong> email address if you opt in to communications</li>
              </ul>
              <h3 className="mt-6 text-lg font-semibold text-slate-950">1.2 Automatically Collected Information</h3>
              <ul className="mt-3 ml-6 list-disc space-y-2">
                <li><strong className="text-slate-950">Usage data:</strong> pages visited, features used, templates viewed, AI generations and exports performed</li>
                <li><strong className="text-slate-950">Device information:</strong> browser type, operating system, screen resolution</li>
                <li><strong className="text-slate-950">Analytics data:</strong> usage events linked to your account (such as pages visited, templates viewed, and actions taken) to help us improve the product. If analytics is enabled, events may be associated with your user ID.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">2. How We Use Your Information</h2>
              <ul className="mt-3 ml-6 list-disc space-y-2">
                <li>Provide, maintain, and improve the React Email Builder service</li>
                <li>Process AI generation requests and render email previews</li>
                <li>Manage your account, billing, and subscription</li>
                <li>Send service-related communications (account verification, billing receipts, security alerts)</li>
                <li>Send product updates and tips if you opt in to the newsletter</li>
                <li>Detect, prevent, and address fraud, abuse, and security issues</li>
                <li>Analyse usage patterns to improve features and user experience</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">3. Data Storage & Security</h2>
              <p className="mt-3">Your data is stored on Cloudflare infrastructure (Workers, D1 database, R2 storage) within the European Union. We implement industry-standard security measures including:</p>
              <ul className="mt-3 ml-6 list-disc space-y-2">
                <li>HTTPS encryption for all data in transit</li>
                <li>Bcrypt-hashed passwords — we never store plaintext passwords</li>
                <li>HTTP-only, Secure session cookies</li>
                <li>Payment data handled entirely by Stripe (PCI DSS Level 1 compliant)</li>
                <li>Regular security reviews and dependency audits</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">4. Third-Party Services</h2>
              <p className="mt-3">We use the following third-party services that may process your data:</p>
              <ul className="mt-3 ml-6 list-disc space-y-3">
                <li><strong className="text-slate-950">Stripe</strong> — payment processing and subscription management. Stripe processes your card details directly; we only receive a token confirming payment. <a href="https://stripe.com/privacy" className="text-sky-700 underline hover:text-sky-600">Stripe Privacy Policy</a></li>
                <li><strong className="text-slate-950">Google (Gemini)</strong> — AI generation. Prompts you submit are sent to Google's Gemini API to generate email templates. Google's data retention policies apply to those requests. <a href="https://policies.google.com/privacy" className="text-sky-700 underline hover:text-sky-600">Google Privacy Policy</a></li>
                <li><strong className="text-slate-950">Cloudflare</strong> — hosting, CDN, and security. Cloudflare may process request data for DDoS protection and performance. <a href="https://www.cloudflare.com/privacypolicy/" className="text-sky-700 underline hover:text-sky-600">Cloudflare Privacy Policy</a></li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">5. AI Generation & Your Content</h2>
              <p className="mt-3">When you use the AI email builder, your prompt is sent to Google's Gemini API for processing. We store your prompt and the generated result in our database to track usage, prevent abuse, and improve the service. Google may also retain prompts according to their own privacy policy. Generated email templates are stored in your account drafts if you choose to save them. You can delete your saved drafts at any time from the dashboard.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">6. Your Rights</h2>
              <p className="mt-3">Under UK GDPR and applicable data protection laws, you have the right to:</p>
              <ul className="mt-3 ml-6 list-disc space-y-2">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data ("right to be forgotten")</li>
                <li>Object to processing of your data</li>
                <li>Data portability — receive your data in a structured format</li>
                <li>Withdraw consent for marketing communications at any time</li>
              </ul>
              <p className="mt-3">To exercise any of these rights, contact us at <a href="mailto:privacy@reactemailbuilder.com" className="text-sky-700 underline hover:text-sky-600">privacy@reactemailbuilder.com</a>.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">7. Cookies</h2>
              <p className="mt-3">We use minimal, essential cookies:</p>
              <ul className="mt-3 ml-6 list-disc space-y-2">
                <li><strong className="text-slate-950">Session cookie</strong> — authenticates your logged-in session (HTTP-only, Secure)</li>
                <li><strong className="text-slate-950">CSRF token</strong> — prevents cross-site request forgery attacks</li>
              </ul>
              <p className="mt-3">We do not use advertising cookies or tracking pixels. We may use privacy-respecting analytics that associates usage events with your account to measure and improve the product. You can request DNT (Do Not Track) to limit analytics collection.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">8. Data Retention</h2>
              <p className="mt-3">We retain your account data for as long as your account is active. If you delete your account, we will delete your personal data within 30 days, except where we are required to retain it for legal or accounting purposes (e.g., billing records for tax compliance, retained for up to 6 years).</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">9. International Transfers</h2>
              <p className="mt-3">Our primary infrastructure is within the EU (Cloudflare EU regions). However, AI generation requests are processed by Google, which may involve transfers to the US under Standard Contractual Clauses. Stripe also processes some data in the US under appropriate safeguards.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">10. Children's Privacy</h2>
              <p className="mt-3">React Email Builder is not intended for use by anyone under the age of 16. We do not knowingly collect personal data from children.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">11. Changes to This Policy</h2>
              <p className="mt-3">We may update this privacy policy from time to time. We will notify you of material changes by posting a notice on our website or sending you an email. Continued use of the service after changes constitutes acceptance of the updated policy.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">12. Contact</h2>
              <p className="mt-3">If you have questions about this privacy policy or your data, contact us at:</p>
              <p className="mt-2"><a href="mailto:privacy@reactemailbuilder.com" className="text-sky-700 underline hover:text-sky-600">privacy@reactemailbuilder.com</a></p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}